import {
  Check,
  Close,
  ContentCopy,
  DoneAll,
  PhoneIphone,
  QrCode,
} from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import QRCode from 'qrcode';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import pixLogo from '../../assets/images/pix_logo.png';
import { Button, Modal } from '../../components';
import { LOGIN } from '../../routes';
import {
  cancelPublicDebtPix,
  getPublicPaymentStatus,
  payPublicDebtPix,
  sendPublicPaymentReceipt,
  PendingPublicPix,
  PublicDebt,
  resolveAdminDebtToken,
  searchPublicDebts,
} from '../../services/PublicDebts';
import { logo, palette, title } from '../../theme';
import { handleDateTime, handleMoney } from '../../utils';
import {
  StyledDebtAmount,
  StyledDebtBody,
  StyledDebtCheckbox,
  StyledDebtHeader,
  StyledDebtItem,
  StyledDebtList,
  StyledDebtMeta,
  StyledLogo,
  StyledMain,
  StyledPaymentStatusBanner,
  StyledPendingPixCard,
  StyledPixCodeButton,
  StyledPixLogo,
  StyledPixStep,
  StyledQr,
  StyledSelectedTotal,
  StyledSelectedTotalLabel,
  StyledSelectedTotalMeta,
  StyledSelectedTotalValue,
  StyledWrapper,
} from './styles';
import { TurnstileWidget } from './TurnstileWidget';

type Step = 'consult' | 'review' | 'pay' | 'done';
type LookupMode = 'plate' | 'notification';

const onlyDigits = (value: string) => value.replace(/\D/g, '');

const plateLooksValid = (plate: string) => {
  const normalized = plate.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  return (
    /^[A-Z]{3}\d{4}$/.test(normalized) ||
    /^[A-Z]{3}\d[A-Z]\d{2}$/.test(normalized)
  );
};

const apiErrorMessage = (error: unknown, fallback: string) => {
  const data = (error as { response?: { data?: { data?: string } } })?.response
    ?.data?.data;
  return typeof data === 'string' && data ? data : fallback;
};

const PAYMENT_PARAM = 'payment';

const isPublicStatusToken = (value: string) => /^[a-f0-9]{64}$/i.test(value);

const emailLooksValid = (value: string) =>
  /^[a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z]{2,4}$/.test(value.trim());

export const Quitacao: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const adminToken = searchParams.get('token') || '';
  const paymentFromUrl = searchParams.get(PAYMENT_PARAM) || '';
  const restoredPayment = isPublicStatusToken(paymentFromUrl)
    ? paymentFromUrl
    : '';

  const [step, setStep] = useState<Step>(restoredPayment ? 'pay' : 'consult');
  const [lookupMode, setLookupMode] = useState<LookupMode>('plate');
  const [plate, setPlate] = useState('');
  const [notificationId, setNotificationId] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaReset, setCaptchaReset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [debts, setDebts] = useState<PublicDebt[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [pixString, setPixString] = useState('');
  const [pixBase64, setPixBase64] = useState('');
  const [payAmount, setPayAmount] = useState<number | null>(null);
  const [expiresAt, setExpiresAt] = useState('');
  const [statusToken, setStatusToken] = useState(restoredPayment);
  const [situation, setSituation] = useState('PENDENTE');
  const [pendingPayments, setPendingPayments] = useState<PendingPublicPix[]>(
    [],
  );
  const [pendingOpen, setPendingOpen] = useState(false);
  const [cancellingToken, setCancellingToken] = useState('');
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptEmail, setReceiptEmail] = useState('');
  const [sendingReceipt, setSendingReceipt] = useState(false);
  const announcedSituation = useRef('');

  const selectedDebts = useMemo(
    () => debts.filter((debt) => selectedIds.includes(debt.id)),
    [debts, selectedIds],
  );
  const selectedTotal = useMemo(
    () =>
      selectedDebts.reduce((sum, debt) => sum + Number(debt.amount || 0), 0),
    [selectedDebts],
  );
  const shownTotal = payAmount != null ? payAmount : selectedTotal;

  const showDebts = (list: PublicDebt[]) => {
    setDebts(list);
    setSelectedIds(list.map((debt) => debt.id));
  };

  const toggleDebt = (id: number) => {
    setSelectedIds((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id],
    );
  };

  const allSelected = debts.length > 0 && selectedIds.length === debts.length;

  const toggleAllDebts = () => {
    setSelectedIds(allSelected ? [] : debts.map((debt) => debt.id));
  };

  const resetCaptcha = useCallback(() => {
    setCaptchaToken('');
    setCaptchaReset((value) => value + 1);
  }, []);

  const clearAdminToken = useCallback(() => {
    if (!searchParams.get('token')) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.delete('token');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const setPaymentInUrl = useCallback(
    (token: string) => {
      const next = new URLSearchParams(searchParams);
      next.delete('token');
      next.set(PAYMENT_PARAM, token);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const clearPaymentFromUrl = useCallback(() => {
    if (!searchParams.get(PAYMENT_PARAM)) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.delete(PAYMENT_PARAM);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleAdminResolve = async () => {
    if (!adminToken) {
      return;
    }
    if (!captchaToken) {
      toast.warning('Complete o captcha para continuar.');
      return;
    }

    setLoading(true);
    try {
      const res = await resolveAdminDebtToken({
        token: adminToken,
        captchaToken,
      });
      if (res.data.response !== 'success') {
        toast.info('Não foi possível abrir o link. Consulte manualmente.');
        clearAdminToken();
        resetCaptcha();
        return;
      }

      const list = res.data.data?.debts || [];
      resetCaptcha();

      if (list.length === 0) {
        toast.info(
          'Link indisponível ou sem débitos em aberto. Consulte por placa ou notificação.',
        );
        clearAdminToken();
        return;
      }

      showDebts(list);
      setStep('review');
    } catch (error) {
      toast.error(
        apiErrorMessage(error, 'Falha ao abrir o link. Tente novamente.'),
      );
      resetCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    const normalizedPlate = plate.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    const notificationDigits = onlyDigits(notificationId);
    const notificationNumber = Number(notificationDigits);

    if (lookupMode === 'plate') {
      if (!normalizedPlate) {
        setFieldError('Informe a placa.');
        return;
      }
      if (!plateLooksValid(normalizedPlate)) {
        setFieldError('Placa inválida. Use o formato ABC1D23 ou ABC1234.');
        return;
      }
    } else {
      if (!notificationDigits) {
        setFieldError('Informe o código da irregularidade.');
        return;
      }
      if (notificationNumber < 1) {
        setFieldError('Código da irregularidade inválido.');
        return;
      }
    }

    setFieldError('');

    if (!captchaToken) {
      toast.warning('Complete o captcha para continuar.');
      return;
    }

    setLoading(true);
    try {
      const search = await searchPublicDebts(
        lookupMode === 'plate'
          ? { plate: normalizedPlate, captchaToken }
          : { notificationId: notificationNumber, captchaToken },
      );

      if (search.data.response !== 'success') {
        toast.error('Não foi possível consultar os débitos.');
        resetCaptcha();
        return;
      }

      const list = search.data.data?.debts || [];
      showDebts(list);
      resetCaptcha();

      if (list.length === 0) {
        toast.info('Nenhum débito em aberto encontrado.');
        return;
      }
      setStep('review');
    } catch (error) {
      toast.error(
        apiErrorMessage(error, 'Falha na consulta. Tente novamente.'),
      );
      resetCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const enterPayStep = (payload: {
    statusToken: string;
    pixString?: string;
    expiresAt?: string | null;
  }) => {
    const nextStatusToken = payload.statusToken || '';
    setPixString(payload.pixString || '');
    setPixBase64('');
    setExpiresAt(payload.expiresAt || '');
    setStatusToken(nextStatusToken);
    setSituation('PENDENTE');
    announcedSituation.current = '';
    setPendingPayments([]);
    setPendingOpen(false);
    setStep('pay');
    if (isPublicStatusToken(nextStatusToken)) {
      setPaymentInUrl(nextStatusToken);
    }
  };

  const handlePay = async () => {
    if (selectedDebts.length < 1) {
      toast.warning('Selecione ao menos uma irregularidade.');
      return;
    }
    if (!captchaToken) {
      toast.warning('Complete o captcha para gerar o Pix.');
      return;
    }

    setLoading(true);
    try {
      const pay = await payPublicDebtPix({
        paymentTokens: selectedDebts.map((debt) => debt.paymentToken),
        captchaToken,
      });
      if (pay.data.response !== 'success' || !pay.data.data) {
        toast.error('Não foi possível gerar o Pix.');
        resetCaptcha();
        return;
      }

      const openPayments = pay.data.data.pendingPayments || [];
      if (openPayments.length > 0) {
        setPendingPayments(openPayments);
        setPendingOpen(true);
        resetCaptcha();
        return;
      }

      const nextStatusToken = pay.data.data.statusToken || '';
      if (!isPublicStatusToken(nextStatusToken)) {
        toast.error('Não foi possível gerar o Pix.');
        resetCaptcha();
        return;
      }

      setPayAmount(selectedTotal);
      enterPayStep({
        statusToken: nextStatusToken,
        pixString: pay.data.data.pixString,
        expiresAt: pay.data.data.expiresAt,
      });
      resetCaptcha();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Não foi possível gerar o Pix.'));
      resetCaptcha();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (step !== 'pay' || !statusToken) {
      return undefined;
    }

    let stopped = false;
    const tick = async () => {
      try {
        const res = await getPublicPaymentStatus(statusToken);
        if (stopped || res.data.response !== 'success') {
          return;
        }
        const data = res.data.data;
        if (data?.expiresAt) {
          setExpiresAt(data.expiresAt);
        }
        if (data?.amount != null && data.amount !== '') {
          const parsedAmount = Number(data.amount);
          if (!Number.isNaN(parsedAmount)) {
            setPayAmount(parsedAmount);
          }
        }
        if (data?.pixString) {
          setPixString(data.pixString);
        }
        const next = data?.situation || 'PENDENTE';
        setSituation(next);
        if (next === announcedSituation.current) {
          return;
        }
        announcedSituation.current = next;
        if (next === 'PAGA') {
          clearPaymentFromUrl();
          setStep('done');
          toast.success('Pagamento confirmado.');
        } else if (next === 'EXPIRADA' || next === 'CANCELADA') {
          toast.warning('Pix expirado ou cancelado. Gere um novo.');
        }
      } catch {
        // Poll silencioso: 404/rede não derrubam a tela.
      }
    };

    tick();
    const id = window.setInterval(tick, 5000);
    return () => {
      stopped = true;
      window.clearInterval(id);
    };
  }, [step, statusToken, clearPaymentFromUrl]);

  useEffect(() => {
    if (!pixString) {
      return undefined;
    }

    let cancelled = false;
    QRCode.toDataURL(pixString, { margin: 1, width: 280, errorCorrectionLevel: 'M' })
      .then((url) => {
        if (!cancelled) {
          setPixBase64(url);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPixBase64('');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [pixString]);

  const resumePendingPix = (payment: PendingPublicPix) => {
    if (!isPublicStatusToken(payment.statusToken)) {
      toast.error('Pix pendente inválido.');
      return;
    }
    setPayAmount(null);
    enterPayStep({
      statusToken: payment.statusToken,
      pixString: payment.pixString,
      expiresAt: payment.expiresAt,
    });
  };

  const cancelPendingPix = async (payment: PendingPublicPix) => {
    if (cancellingToken) {
      return;
    }
    setCancellingToken(payment.statusToken);
    try {
      await cancelPublicDebtPix(payment.statusToken);
      setPendingPayments((current) =>
        current.filter((item) => item.statusToken !== payment.statusToken),
      );
      setPendingOpen(true);
      resetCaptcha();
      toast.success('Pix cancelado.');
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Não foi possível cancelar o Pix.'));
    } finally {
      setCancellingToken('');
    }
  };

  const handleCancelPix = async () => {
    if (!statusToken || cancelling || situation !== 'PENDENTE') {
      return;
    }

    setCancelling(true);
    try {
      const res = await cancelPublicDebtPix(statusToken);
      const next = res.data.data?.situation || 'CANCELADA';
      if (res.data.data?.expiresAt) {
        setExpiresAt(res.data.data.expiresAt);
      }
      setSituation(next);
      if (announcedSituation.current !== next) {
        announcedSituation.current = next;
        toast.success('Pix cancelado com sucesso.');
      }
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Não foi possível cancelar o Pix.'));
    } finally {
      setCancelling(false);
    }
  };

  const copyPix = async () => {
    if (!pixString) {
      toast.warning('Código Pix indisponível.');
      return;
    }
    try {
      await navigator.clipboard.writeText(pixString);
      toast.info('Código Pix copiado.');
    } catch {
      toast.error('Não foi possível copiar. Selecione o código manualmente.');
    }
  };

  const restart = () => {
    clearAdminToken();
    clearPaymentFromUrl();
    setStep('consult');
    setDebts([]);
    setSelectedIds([]);
    setPixString('');
    setPixBase64('');
    setPayAmount(null);
    setPlate('');
    setNotificationId('');
    setStatusToken('');
    setExpiresAt('');
    setSituation('PENDENTE');
    setPendingPayments([]);
    setPendingOpen(false);
    setFieldError('');
    setReceiptOpen(false);
    setReceiptEmail('');
    resetCaptcha();
  };

  const closeReceiptModal = () => {
    if (sendingReceipt) {
      return;
    }
    setReceiptOpen(false);
    setReceiptEmail('');
  };

  const handleSendReceipt = async () => {
    const email = receiptEmail.trim();
    if (!emailLooksValid(email) || sendingReceipt) {
      return;
    }
    if (!isPublicStatusToken(statusToken)) {
      toast.error('Não foi possível identificar o pagamento.');
      return;
    }

    setSendingReceipt(true);
    try {
      await sendPublicPaymentReceipt(statusToken, email);
      toast.success('Comprovante enviado com sucesso.');
      setReceiptEmail('');
      setReceiptOpen(false);
    } catch (error) {
      toast.error(
        apiErrorMessage(error, 'Não foi possível enviar o comprovante.'),
      );
    } finally {
      setSendingReceipt(false);
    }
  };

  const goToLogin = () => {
    clearAdminToken();
    clearPaymentFromUrl();
    navigate(LOGIN);
  };

  const isAdminLink = Boolean(adminToken) && step === 'consult';
  const hasQr = Boolean(pixBase64);
  const qrSrc = hasQr
    ? pixBase64.startsWith('data:')
      ? pixBase64
      : `data:image/png;base64,${pixBase64}`
    : '';

  return (
    <StyledWrapper>
      <StyledMain>
        {step !== 'pay' && <StyledLogo src={logo} alt={title} />}
        {step !== 'pay' && (
          <>
            <Typography variant='h6' textAlign='center' fontWeight={700}>
              Quitar irregularidades
            </Typography>
            <Typography
              variant='body2'
              textAlign='center'
              color='text.secondary'
            >
              {isAdminLink
                ? 'Confirme o captcha para ver os débitos deste link e pagar via Pix.'
                : 'Consulte por placa ou código da irregularidade e pague via Pix.'}
            </Typography>
          </>
        )}

        {step === 'consult' && isAdminLink && (
          <>
            <TurnstileWidget
              resetKey={captchaReset}
              onToken={setCaptchaToken}
              onExpire={() => setCaptchaToken('')}
            />
            <Button
              label={loading ? 'Abrindo...' : 'Ver débitos'}
              loading={loading}
              disabled={loading}
              onClick={handleAdminResolve}
              fullWidth
              variant='contained'
            />
            <Button
              label='Consultar manualmente'
              onClick={() => {
                clearAdminToken();
                resetCaptcha();
              }}
              fullWidth
              variant='text'
            />
            <Button
              label='Voltar para o login'
              onClick={goToLogin}
              fullWidth
              variant='text'
            />
          </>
        )}

        {step === 'consult' && !isAdminLink && (
          <>
            <ToggleButtonGroup
              exclusive
              fullWidth
              size='small'
              value={lookupMode}
              onChange={(_, value: LookupMode | null) => {
                if (value) {
                  setLookupMode(value);
                  setFieldError('');
                  setPlate('');
                  setNotificationId('');
                }
              }}
            >
              <ToggleButton value='plate'>Placa</ToggleButton>
              <ToggleButton value='notification'>Irregularidade</ToggleButton>
            </ToggleButtonGroup>

            {lookupMode === 'plate' ? (
              <TextField
                label='Placa'
                value={plate}
                onChange={(event) => {
                  setPlate(event.target.value.toUpperCase().slice(0, 8));
                  if (fieldError) setFieldError('');
                }}
                error={Boolean(fieldError)}
                helperText={fieldError}
                fullWidth
                required
                inputProps={{ autoCapitalize: 'characters' }}
              />
            ) : (
              <TextField
                label='Código da irregularidade'
                value={notificationId}
                onChange={(event) => {
                  setNotificationId(
                    onlyDigits(event.target.value).slice(0, 12),
                  );
                  if (fieldError) setFieldError('');
                }}
                error={Boolean(fieldError)}
                helperText={fieldError}
                fullWidth
                required
                inputMode='numeric'
              />
            )}

            <TurnstileWidget
              resetKey={captchaReset}
              onToken={setCaptchaToken}
              onExpire={() => setCaptchaToken('')}
            />

            <Button
              label={loading ? 'Buscando...' : 'Consultar'}
              loading={loading}
              disabled={loading}
              onClick={handleSearch}
              fullWidth
              variant='contained'
            />
            <Button
              label='Voltar para o login'
              onClick={goToLogin}
              fullWidth
              variant='text'
            />
          </>
        )}

        {step === 'review' && (
          <>
            <StyledDebtHeader>
              <Typography fontWeight={600}>Débitos em aberto</Typography>
              <Button
                label={allSelected ? 'Desmarcar todas' : 'Marcar todas'}
                onClick={toggleAllDebts}
                variant='text'
                size='small'
              />
            </StyledDebtHeader>
            <Typography variant='body2' color='text.secondary'>
              Marque uma ou mais. Um único Pix quita todas as selecionadas.
            </Typography>
            <StyledDebtList role='listbox' aria-multiselectable='true'>
              {debts.map((debt) => {
                const selected = selectedIds.includes(debt.id);
                return (
                  <StyledDebtItem
                    key={debt.id}
                    type='button'
                    role='option'
                    selected={selected}
                    aria-selected={selected}
                    onClick={() => toggleDebt(debt.id)}
                  >
                    <StyledDebtCheckbox selected={selected} aria-hidden>
                      {selected ? <Check sx={{ fontSize: 16 }} /> : null}
                    </StyledDebtCheckbox>
                    <StyledDebtBody>
                      <Typography
                        color='primary'
                        sx={{ fontWeight: 700, fontSize: 15, lineHeight: 1.25 }}
                      >
                        #{debt.id}
                      </Typography>
                      <StyledDebtMeta>
                        <span>{debt.plateMasked}</span>
                        <span aria-hidden>·</span>
                        <span>{handleDateTime(debt.date)}</span>
                      </StyledDebtMeta>
                    </StyledDebtBody>
                    <StyledDebtAmount>
                      {handleMoney(debt.amount)}
                    </StyledDebtAmount>
                  </StyledDebtItem>
                );
              })}
            </StyledDebtList>
            <StyledSelectedTotal>
              <StyledSelectedTotalLabel>Total selecionado</StyledSelectedTotalLabel>
              <StyledSelectedTotalValue>
                {handleMoney(selectedTotal)}
              </StyledSelectedTotalValue>
              <StyledSelectedTotalMeta>
                {selectedDebts.length}{' '}
                {selectedDebts.length === 1
                  ? 'irregularidade'
                  : 'irregularidades'}
              </StyledSelectedTotalMeta>
            </StyledSelectedTotal>

            {!pendingOpen && (
              <TurnstileWidget
                resetKey={captchaReset}
                onToken={setCaptchaToken}
                onExpire={() => setCaptchaToken('')}
              />
            )}

            <Button
              label={loading ? 'Gerando Pix...' : 'Pagar com Pix'}
              loading={loading}
              disabled={loading || selectedDebts.length < 1}
              onClick={handlePay}
              fullWidth
              variant='contained'
            />
            <Button
              label='Nova consulta'
              onClick={restart}
              fullWidth
              variant='outlined'
            />
            <Button
              label='Voltar para o login'
              onClick={goToLogin}
              fullWidth
              variant='text'
            />
          </>
        )}

        {step === 'pay' && (
          <>
            <StyledPixLogo src={pixLogo} alt='Pix' />
            <Typography
              textAlign='center'
              sx={{
                color: palette.text.main,
                fontSize: 24,
                fontWeight: 'bold',
              }}
            >
              Total: {handleMoney(shownTotal)}
            </Typography>
            <Typography
              textAlign='center'
              sx={{ color: palette.text.main, fontSize: 18 }}
            >
              Seu Pix expira em:
            </Typography>
            <Typography
              textAlign='center'
              sx={{
                color: palette.text.main,
                fontSize: 18,
                fontWeight: 'bold',
                mt: -1,
              }}
            >
              {expiresAt ? handleDateTime(expiresAt) : '—'}
            </Typography>

            <StyledPaymentStatusBanner
              role='status'
              aria-live='polite'
              tone={
                situation === 'PAGA'
                  ? 'success'
                  : situation === 'EXPIRADA' || situation === 'CANCELADA'
                  ? 'danger'
                  : 'pending'
              }
            >
              {situation === 'PENDENTE' && (
                <CircularProgress size={20} sx={{ color: '#5c5200' }} />
              )}
              <Typography
                textAlign='center'
                sx={{ fontSize: 15, fontWeight: 700, lineHeight: 1.3 }}
              >
                {situation === 'PENDENTE' && 'Aguardando pagamento'}
                {situation === 'PAGA' && 'Pagamento confirmado'}
                {situation === 'EXPIRADA' && 'Pix expirado'}
                {situation === 'CANCELADA' && 'Pix cancelado'}
                {!['PENDENTE', 'PAGA', 'EXPIRADA', 'CANCELADA'].includes(
                  situation,
                ) && `Status: ${situation}`}
              </Typography>
            </StyledPaymentStatusBanner>
            {situation === 'PENDENTE' && (
              <Typography
                textAlign='center'
                sx={{
                  color: palette.text.main,
                  fontSize: 13,
                  mt: -1,
                }}
              >
                Esta tela atualiza automaticamente após a confirmação.
              </Typography>
            )}

            <StyledPixStep>
              <ContentCopy color='primary' />
              <Typography
                sx={{ color: palette.text.main, fontSize: 15, flex: 1 }}
              >
                {hasQr ? (
                  <>
                    1. Utilize o <b>código QR</b> abaixo ou clique em{' '}
                    <b>Copiar Código Pix</b> ou diretamente no texto do código
                    abaixo
                  </>
                ) : (
                  <>
                    1. Clique em <b>Copiar Código Pix</b> ou no texto abaixo e
                    cole no app do seu banco
                  </>
                )}
              </Typography>
            </StyledPixStep>

            {hasQr && <StyledQr src={qrSrc} alt='QR Code Pix' />}

            {hasQr && (
              <Typography
                textAlign='center'
                sx={{ color: palette.text.main, fontSize: 15 }}
              >
                - ou -
              </Typography>
            )}

            <Button
              variant='contained'
              label='Copiar Código Pix'
              onClick={copyPix}
              color='primary'
              startIcon={<ContentCopy />}
              fullWidth
            />

            <Typography
              textAlign='center'
              sx={{ color: palette.text.main, fontSize: 15 }}
            >
              - ou -
            </Typography>

            <StyledPixCodeButton
              type='button'
              onClick={copyPix}
              emphasized={!hasQr}
            >
              <Typography
                noWrap
                sx={{ color: palette.text.main, fontSize: 14, flex: 1 }}
              >
                {pixString || 'Código Pix indisponível'}
              </Typography>
              <Typography
                color='primary'
                sx={{ fontSize: 15, fontWeight: 'bold', flexShrink: 0 }}
              >
                Copiar
              </Typography>
            </StyledPixCodeButton>

            <StyledPixStep>
              <PhoneIphone color='primary' />
              <Typography
                sx={{ color: palette.text.main, fontSize: 15, flex: 1 }}
              >
                2. Abra o app do seu banco ou instituição financeira e entre no
                ambiente <b>Pix</b>
              </Typography>
            </StyledPixStep>

            <StyledPixStep>
              <QrCode color='primary' />
              <Typography
                sx={{ color: palette.text.main, fontSize: 15, flex: 1 }}
              >
                3. Escolha a opção <b>Pix Copia e Cola</b> e cole o código
                copiado
              </Typography>
            </StyledPixStep>

            <StyledPixStep>
              <Check color='primary' />
              <Typography
                sx={{ color: palette.text.main, fontSize: 15, flex: 1 }}
              >
                4. Confirme as informações e finalize o pagamento
              </Typography>
            </StyledPixStep>

            <StyledPixStep>
              <DoneAll color='primary' />
              <Typography
                sx={{ color: palette.text.main, fontSize: 15, flex: 1 }}
              >
                5. Aguarde nesta tela — a confirmação é automática
              </Typography>
            </StyledPixStep>

            {situation === 'PENDENTE' && (
              <>
                <StyledPixStep>
                  <Close color='primary' />
                  <Typography
                    sx={{ color: palette.text.main, fontSize: 15, flex: 1 }}
                  >
                    6. Caso seja necessário, cancele o Pix em aberto clicando em
                    Cancelar Pix abaixo
                  </Typography>
                </StyledPixStep>
                <Button
                  variant='contained'
                  label='Cancelar Pix'
                  onClick={handleCancelPix}
                  loading={cancelling}
                  disabled={cancelling}
                  color='secondary'
                  startIcon={<Close />}
                  fullWidth
                />
              </>
            )}

            {(situation === 'EXPIRADA' || situation === 'CANCELADA') && (
              <>
                <Button
                  label='Nova consulta'
                  onClick={restart}
                  fullWidth
                  variant='contained'
                />
                <Button
                  label='Voltar para o login'
                  onClick={goToLogin}
                  fullWidth
                  variant='text'
                />
              </>
            )}
          </>
        )}

        {step === 'done' && (
          <>
            <Typography variant='h6' textAlign='center' color='primary'>
              Pagamento confirmado
            </Typography>
            <Typography textAlign='center' color='text.secondary'>
              Sua irregularidade foi quitada. Você já pode fechar esta página.
            </Typography>
            <Button
              label='Enviar comprovante por e-mail'
              onClick={() => setReceiptOpen(true)}
              fullWidth
              variant='contained'
              color='secondary'
            />
            <Button
              label='Consultar outro débito'
              onClick={restart}
              fullWidth
              variant='contained'
            />
            <Button
              label='Voltar para o login'
              onClick={goToLogin}
              fullWidth
              variant='text'
            />
          </>
        )}
      </StyledMain>
      <Modal
        open={receiptOpen}
        onClose={closeReceiptModal}
        title='Enviar comprovante'
        width={440}
      >
        <Box>
          <Typography sx={{ mb: 2, color: palette.text.main }}>
            Informe o e-mail que vai receber o comprovante deste pagamento.
          </Typography>
          <TextField
            label='E-mail'
            type='email'
            sx={{ marginTop: 3 }}
            value={receiptEmail}
            onChange={(event) => setReceiptEmail(event.target.value)}
            error={receiptEmail.trim() !== '' && !emailLooksValid(receiptEmail)}
            helperText={
              receiptEmail.trim() !== '' && !emailLooksValid(receiptEmail)
                ? 'Informe um e-mail válido.'
                : ' '
            }
            fullWidth
            disabled={sendingReceipt}
            autoFocus
          />
          <Button
            label={sendingReceipt ? 'Enviando...' : 'Enviar'}
            onClick={handleSendReceipt}
            loading={sendingReceipt}
            disabled={sendingReceipt || !emailLooksValid(receiptEmail)}
            fullWidth
            variant='contained'
          />
          <Button
            label='Fechar'
            onClick={closeReceiptModal}
            disabled={sendingReceipt}
            fullWidth
            variant='outlined'
          />
        </Box>
      </Modal>
      <Modal
        open={pendingOpen}
        onClose={() => {
          if (!loading && !cancellingToken) {
            setPendingOpen(false);
          }
        }}
        title='Pagamento em aberto'
        width={440}
      >
        <Box>
          <Typography sx={{ mb: 2, color: palette.text.main }}>
            {pendingPayments.length > 0
              ? 'Já existe Pix em aberto para uma ou mais irregularidades selecionadas. Você pode pagar o existente ou cancelar e gerar outro.'
              : 'Os Pix em aberto foram cancelados. Complete o captcha abaixo e gere um novo.'}
          </Typography>

          {pendingPayments.map((payment) => {
            const ids =
              payment.matchedIds?.length > 0
                ? payment.matchedIds
                : payment.irregularityIds;
            const isCancellingThis = cancellingToken === payment.statusToken;
            return (
              <StyledPendingPixCard key={payment.statusToken}>
                <Typography fontWeight={700} color='primary'>
                  Irregularidades {ids.map((id) => `#${id}`).join(', ')}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Gerado em {handleDateTime(payment.createdAt)}
                  {payment.expiresAt
                    ? ` · Expira em ${handleDateTime(payment.expiresAt)}`
                    : ''}
                </Typography>
                <Button
                  label='Pagar este Pix'
                  onClick={() => resumePendingPix(payment)}
                  disabled={Boolean(cancellingToken) || loading}
                  fullWidth
                  variant='contained'
                />
                <Button
                  label={
                    isCancellingThis
                      ? 'Cancelando...'
                      : 'Cancelar e gerar outro'
                  }
                  onClick={() => cancelPendingPix(payment)}
                  disabled={Boolean(cancellingToken) || loading}
                  loading={isCancellingThis}
                  fullWidth
                  variant='outlined'
                />
              </StyledPendingPixCard>
            );
          })}

          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ mb: 1, mt: pendingPayments.length > 0 ? 1 : 0 }}
          >
            Para gerar um Pix novo, complete o captcha e toque em Gerar novo
            Pix.
          </Typography>
          <TurnstileWidget
            resetKey={captchaReset}
            onToken={setCaptchaToken}
            onExpire={() => setCaptchaToken('')}
          />
          <Button
            label={loading ? 'Gerando...' : 'Gerar novo Pix'}
            onClick={handlePay}
            loading={loading}
            disabled={loading || Boolean(cancellingToken) || !captchaToken}
            fullWidth
            variant='contained'
            color='secondary'
            style={{ marginTop: 8 }}
          />
        </Box>
      </Modal>
    </StyledWrapper>
  );
};
